import React, { useState } from "react";
import { Box, Card, CardContent, Checkbox, CircularProgress, FormControlLabel, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { bucketTotalsOf } from "../methodologyUtils";
import { fieldImpactLabel, SEVERITY_COLOR } from "../labels";
import { ImpactText } from "../badges";
import EntityGapsPanel from "../panels/EntityGapsPanel";
import FieldDrillPanel from "../panels/FieldDrillPanel";

// DATOS (spec §6, Opción A): the diagnosis zone. Two perspectives:
//   · Por campo   → ¿qué tipo de dato tiene problemas y qué impacto tiene? (from gapFields)
//   · Por entidad → ¿qué ocurre con este artículo/material/proveedor? (EntityGapsPanel)
// Not a worklist — it answers "¿dónde están los problemas?".

const useStyles = makeStyles((theme) => ({
  toggleRow: { display: "flex", gap: theme.spacing(1), marginBottom: theme.spacing(2) },
  toggle: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 18,
    padding: "5px 16px",
    fontSize: 14,
    cursor: "pointer",
    background: theme.palette.background.paper,
    font: "inherit",
  },
  toggleActive: { background: theme.palette.primary.main, color: "#fff", borderColor: theme.palette.primary.main },
  sectionHead: { fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: theme.palette.text.secondary, margin: theme.spacing(2, 0, 1) },
  headerRow: { display: "flex", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#94a3b8", padding: "0 8px 6px" },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: "8px",
    borderTop: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    fontSize: 14,
  },
  colName: { flex: "2 1 0", minWidth: 0, fontWeight: 600 },
  colCov: { flex: "0 0 90px", textAlign: "right", fontWeight: 600 },
  colState: { flex: "1 1 0" },
  colImpact: { flex: "2 1 0" },
  detail: { padding: theme.spacing(1.5, 2), background: theme.palette.grey[50], fontSize: 13 },
  bucketRow: { display: "flex", gap: theme.spacing(3), flexWrap: "wrap", marginBottom: theme.spacing(1) },
  bucket: { fontSize: 13 },
  link: { color: theme.palette.primary.main, cursor: "pointer", fontSize: 13, fontWeight: 600 },
  empty: { color: theme.palette.text.secondary, padding: theme.spacing(2, 0) },
}));

const SECTION_ORDER = ["PRODUCT", "MATERIALS", "RETAIL PACKAGING", "SUPPLIERS"];
const SECTION_LABEL = { PRODUCT: "Artículo", MATERIALS: "Materiales", "RETAIL PACKAGING": "Packaging", SUPPLIERS: "Proveedores", OTROS: "Otros" };

const covColor = (v) => (v == null ? "#cbd5e1" : v >= 80 ? SEVERITY_COLOR.ok : v >= 40 ? SEVERITY_COLOR.warn : SEVERITY_COLOR.critical);

// Short human "Estado" derived from the buckets, worst-first (spec §6.1 "1 falta" / "1 asumible" / "Completo").
const estadoOf = (b) => {
  if (b.faltan) return { text: `${b.faltan} falta${b.faltan > 1 ? "n" : ""}`, color: SEVERITY_COLOR.warn };
  if (b.revisar) return { text: `${b.revisar} inválido${b.revisar > 1 ? "s" : ""}`, color: SEVERITY_COLOR.warn };
  if (b.asumibles) return { text: `${b.asumibles} asumible${b.asumibles > 1 ? "s" : ""}`, color: SEVERITY_COLOR.warn };
  if (b.asumidos) return { text: `${b.asumidos} asumido${b.asumidos > 1 ? "s" : ""}`, color: SEVERITY_COLOR.ok };
  return { text: "Completo", color: SEVERITY_COLOR.ok };
};

const ByFieldView = ({ gapFields, contracted, onDrill, loading, error, initialLevel }) => {
  const classes = useStyles();
  const [showComplete, setShowComplete] = useState(false);
  const [level, setLevel] = useState(initialLevel || "todos"); // todos | L1 | L2 — "¿qué me falta para el 100% de L1/L2?"

  if (loading && !(gapFields || []).length) {
    return (
      <Box textAlign="center" py={4}>
        <CircularProgress size={26} />
      </Box>
    );
  }
  if (error && !(gapFields || []).length) {
    return <Typography color="error" className={classes.empty}>{error}</Typography>;
  }

  // A field is "complete" when it has nothing pending (no faltan / revisar / asumibles).
  const hasPending = (b) => b.faltan + b.revisar + b.asumibles > 0;
  const levelled = (gapFields || []).filter((f) => level === "todos" || f.level === level);
  const all = levelled.map((f) => ({ f, b: bucketTotalsOf(f.statusCounts) }));
  const completeCount = all.filter((r) => !hasPending(r.b)).length;
  const enriched = showComplete ? all : all.filter((r) => hasPending(r.b));
  const bySection = {};
  enriched.forEach((r) => {
    const s = r.f.section || "OTROS";
    (bySection[s] = bySection[s] || []).push(r);
  });
  const sections = [
    ...SECTION_ORDER.filter((s) => bySection[s]),
    ...Object.keys(bySection).filter((s) => !SECTION_ORDER.includes(s)),
  ];

  const attentionCount = all.length - completeCount; // campos con algún pendiente
  const header = (
    <>
      <Typography variant="h6" style={{ fontSize: 18, fontWeight: 700 }}>
        Datos por campo
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
        Comprueba qué información falta y qué metodologías se ven afectadas.
      </Typography>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" style={{ gap: 8 }}>
        <Typography variant="body2" color="textSecondary">
          <strong>{attentionCount}</strong> campo{attentionCount === 1 ? "" : "s"} requiere{attentionCount === 1 ? "" : "n"} atención · {completeCount} completo{completeCount === 1 ? "" : "s"}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox size="small" checked={showComplete} onChange={(e) => setShowComplete(e.target.checked)} />
          }
          label={<span style={{ fontSize: 13 }}>Mostrar completos ({completeCount})</span>}
        />
      </Box>
      <Box display="flex" alignItems="center" mb={1} style={{ gap: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#94a3b8" }}>Nivel de trazabilidad:</span>
        {[
          { k: "todos", l: "Todos" },
          { k: "L1", l: "L1" },
          { k: "L2", l: "L2" },
        ].map((o) => (
          <button
            key={o.k}
            type="button"
            className={`${classes.toggle} ${level === o.k ? classes.toggleActive : ""}`}
            style={{ padding: "2px 12px", fontSize: 13 }}
            onClick={() => setLevel(o.k)}
          >
            {o.l}
          </button>
        ))}
      </Box>
    </>
  );

  if (!all.length) {
    return (
      <>
        {header}
        <Typography className={classes.empty}>Sin datos que mostrar.</Typography>
      </>
    );
  }
  if (!enriched.length) {
    return (
      <>
        {header}
        <Typography className={classes.empty}>
          Todos los campos están completos. Marca «Mostrar completos» para verlos.
        </Typography>
      </>
    );
  }

  return (
    <>
      {header}
      <div className={classes.headerRow}>
        <span className={classes.colName}>Campo</span>
        <span className={classes.colCov}>Cobertura</span>
        <span className={classes.colState}>Estado</span>
        <span className={classes.colImpact}>Impacto</span>
      </div>
      {sections.map((s) => (
        <div key={s}>
          <div className={classes.sectionHead}>
            {SECTION_LABEL[s] || s} · {bySection[s].length}
          </div>
          {bySection[s].map(({ f, b }) => {
            const est = estadoOf(b);
            const impact = fieldImpactLabel(f, { allowed: contracted });
            // Whole row is the single click target → entidades afectadas (§6.1). No extra CTAs.
            return (
              <div
                key={f.fieldDefinitionId}
                className={classes.row}
                title={f.fieldKey}
                onClick={() => onDrill({ fieldKey: f.fieldKey, displayName: f.displayName })}
              >
                <span className={classes.colName}>{f.displayName || f.fieldKey}</span>
                <span className={classes.colCov} style={{ color: covColor(f.coverage) }}>
                  {f.coverage == null ? "—" : f.coverage + "%"}
                </span>
                <span className={classes.colState} style={{ color: est.color, fontWeight: 600 }}>
                  {est.text}
                </span>
                <span className={classes.colImpact}>
                  {impact ? (
                    <ImpactText label={impact} />
                  ) : (
                    // Impacto vacío = NO bloquea (no es "desconocido"): explícito para no confundir.
                    <span style={{ color: "#94a3b8" }} title="No bloquea las metodologías actuales">
                      No bloquea
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
};

const DatosScreen = ({ api, projectId, contracted, data, preset }) => {
  const classes = useStyles();
  const [view, setView] = useState("campo"); // campo | entidad
  const [drill, setDrill] = useState(null);
  // A deep-link from Resumen (e.g. click on L1) preselects the "Por campo" view + level.

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <CardContent style={{ paddingBottom: 16 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            ¿Dónde están los problemas? Explora por tipo de dato o por entidad.
          </Typography>
          <div className={classes.toggleRow} style={{ marginBottom: 0 }}>
            <button
              type="button"
              className={`${classes.toggle} ${view === "campo" ? classes.toggleActive : ""}`}
              onClick={() => setView("campo")}
            >
              Por campo
            </button>
            <button
              type="button"
              className={`${classes.toggle} ${view === "entidad" ? classes.toggleActive : ""}`}
              onClick={() => setView("entidad")}
            >
              Por entidad
            </button>
          </div>
        </CardContent>
      </Card>

      {view === "campo" ? (
        <Card>
          <CardContent>
            <ByFieldView gapFields={data.gapFields} contracted={contracted} onDrill={setDrill} loading={data.loading} error={data.error} initialLevel={preset?.level} />
          </CardContent>
        </Card>
      ) : (
        // "Por entidad" reuses the existing entity browser (its own Card + Artículos | Materiales | Proveedores sub-tabs).
        <EntityGapsPanel
          api={api}
          projectId={projectId}
          contracted={contracted}
          onChanged={() => {
            if (data.reload) {
              data.reload();
              setTimeout(data.reload, 5000); // completeness job is async — catch the recomputed scores
            }
          }}
        />
      )}

      {drill && (
        <FieldDrillPanel
          api={api}
          projectId={projectId}
          field={drill}
          contracted={contracted}
          onClose={() => {
            setDrill(null);
            if (data.reload) data.reload();
          }}
        />
      )}
    </>
  );
};

export default DatosScreen;
